import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Modal: Preview AI-generated schema, ask "Add?", then "Replace or merge?".
 * onConfirm('replace' | 'merge') is called with the user's choice; parent applies the schema.
 */
const GenerateSchemaConfirmModal = ({ open, onClose, generatedFields = [], onConfirm }) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(1)

  useEffect(() => {
    if (open) setStep(1)
  }, [open])

  if (!open) return null

  const handleAdd = () => setStep(2)
  const handleReplace = () => {
    onConfirm('replace')
    onClose()
  }
  const handleMerge = () => {
    onConfirm('merge')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-light-200/20 flex items-center justify-between">
          <h3 className="text-lg font-bold text-dark-300">
            {step === 1 ? t('schemaEditor.generateModal.title') : t('schemaEditor.generateModal.replaceOrMergeTitle')}
          </h3>
          <button type="button" onClick={onClose} className="text-dark-100 hover:text-dark-300">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {step === 1 && (
            <>
              <p className="text-sm text-dark-100 mb-3">{t('schemaEditor.generateModal.previewDesc')}</p>
              <ul className="space-y-2 max-h-48 overflow-y-auto bg-light-100 rounded-lg p-3 text-sm">
                {generatedFields.map((f, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="font-mono text-primary-800">{f.field_name}</span>
                    <span className="text-dark-100 truncate">{f.display_title}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm font-medium text-dark-300 mt-4">{t('schemaEditor.generateModal.addPrompt')}</p>
            </>
          )}
          {step === 2 && (
            <p className="text-sm text-dark-300">{t('schemaEditor.generateModal.replaceOrMergeDesc')}</p>
          )}
        </div>
        <div className="p-4 border-t border-light-200/20 flex flex-wrap gap-2 justify-end">
          {step === 1 && (
            <>
              <button type="button" onClick={onClose} className="btn-secondary">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={handleAdd} className="px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600">
                {t('schemaEditor.generateModal.addButton')}
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button type="button" onClick={() => setStep(1)} className="btn-secondary">
                {t('common.back')}
              </button>
              <button type="button" onClick={handleMerge} className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
                {t('schemaEditor.generateModal.merge')}
              </button>
              <button type="button" onClick={handleReplace} className="px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600">
                {t('schemaEditor.generateModal.replace')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default GenerateSchemaConfirmModal
